import { cn } from '@/shared/lib/cn';
import { formatMoney } from '@/shared/lib/money';
import { etiquetaColumna, type ResumenMedioMes } from '../lib/promesas';

/**
 * Cuentas por Cobrar: cuánto se espera cobrar por caja y por mes (lo vencido
 * aparte). Un bloque por moneda — el monto está en la moneda de la caja y no
 * se convierte. Pedido del cliente 2026-10-20.
 */
export function ResumenPromesas({ resumen, mesActual }: { resumen: ResumenMedioMes; mesActual: string }) {
  if (resumen.monedas.length === 0) return null;
  const { columnas } = resumen;
  return (
    <div className="overflow-x-auto rounded-2xl border border-border bg-card">
      <table className="w-full text-sm">
        <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="whitespace-nowrap px-4 py-2 font-medium">Caja</th>
            {columnas.map((c) => (
              <th
                key={c}
                className={cn(
                  'whitespace-nowrap px-4 py-2 text-right font-medium',
                  c === 'vencido' && 'text-rose-700',
                )}
              >
                {etiquetaColumna(c)}
                {c === mesActual && <span className="normal-case"> (este mes)</span>}
              </th>
            ))}
            <th className="whitespace-nowrap px-4 py-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        {resumen.monedas.map((m) => {
          const moneda = m.moneda === 'USD' ? 'USD' : m.moneda === 'MXN' ? 'MXN' : 'ARS';
          const celda = (v: number | undefined) => (v ? formatMoney(v, moneda) : '—');
          return (
            <tbody key={m.moneda} className="divide-y divide-border">
              {resumen.monedas.length > 1 && (
                <tr className="bg-muted/40">
                  <td colSpan={columnas.length + 2} className="px-4 py-1.5 text-xs font-medium text-muted-foreground">
                    {moneda === 'USD' ? 'Dólares' : 'Pesos'}
                  </td>
                </tr>
              )}
              {m.filas.map((f) => (
                <tr key={f.medio}>
                  <td className="whitespace-nowrap px-4 py-2 text-foreground">{f.medio}</td>
                  {columnas.map((c) => (
                    <td
                      key={c}
                      className={cn(
                        'whitespace-nowrap px-4 py-2 text-right tabular-nums',
                        c === 'vencido' && f.porColumna[c] ? 'text-rose-700' : 'text-muted-foreground',
                      )}
                    >
                      {celda(f.porColumna[c])}
                    </td>
                  ))}
                  <td className="whitespace-nowrap px-4 py-2 text-right font-medium tabular-nums">{celda(f.total)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="px-4 py-2">Total {moneda === 'USD' ? 'US$' : '$'}</td>
                {columnas.map((c) => (
                  <td
                    key={c}
                    className={cn(
                      'whitespace-nowrap px-4 py-2 text-right tabular-nums',
                      c === 'vencido' && m.totalPorColumna[c] && 'text-rose-700',
                    )}
                  >
                    {celda(m.totalPorColumna[c])}
                  </td>
                ))}
                <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums">{celda(m.total)}</td>
              </tr>
            </tbody>
          );
        })}
      </table>
    </div>
  );
}
